
import React, { useState } from 'react';
import { Button } from './Button';
import { PrintableCustomLabel } from './PrintableCustomLabel';
import { DYMO_LABEL_SIZES, DEFAULT_LABEL_SIZE_ID } from '../constants'; // Import label sizes

interface CustomLabelDesignerProps {
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
}

const XCircleIconMini: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-red-500 hover:text-red-700" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" transform="scale(0.83) translate(2,2)" />
    </svg>
);
const PlusCircleIconMini: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-emerald-500 hover:text-emerald-700" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" transform="scale(0.83) translate(2,2)" />
  </svg>
);


export const CustomLabelDesigner: React.FC<CustomLabelDesignerProps> = ({ onOpenPrintModal }) => {
  const defaultSize = DYMO_LABEL_SIZES.find(s => s.id === DEFAULT_LABEL_SIZE_ID) || DYMO_LABEL_SIZES[0];
  const [selectedLabelSizeId, setSelectedLabelSizeId] = useState<string>(defaultSize.id);
  const [labelWidthInches, setLabelWidthInches] = useState<number>(defaultSize.widthInches);
  const [labelHeightInches, setLabelHeightInches] = useState<number>(defaultSize.heightInches);
  
  const [title, setTitle] = useState<string>('Sample Title');
  const [bodyLines, setBodyLines] = useState<string[]>(['Line 1: Details here', 'Line 2: More information']);
  const [footer, setFooter] = useState<string>('Footer Text');
  const [barcodeData, setBarcodeData] = useState<string>('ITEM12345'); 
  const [showBarcode, setShowBarcode] = useState<boolean>(true); 
  const [fontSizePt, setFontSizePt] = useState<number>(8);

  const handleLabelSizeChange = (sizeId: string) => {
    const newSize = DYMO_LABEL_SIZES.find(s => s.id === sizeId);
    if (newSize) {
        setSelectedLabelSizeId(sizeId);
        setLabelWidthInches(newSize.widthInches);
        setLabelHeightInches(newSize.heightInches);
    } else if (sizeId === "custom") {
        setSelectedLabelSizeId("custom");
        // Keep current width/height or reset to a default custom size
        // For now, we let users type in custom dimensions if they select "custom"
    }
  };

  const handleAddBodyLine = () => {
    setBodyLines([...bodyLines, 'New line']);
  };

  const handleRemoveBodyLine = (index: number) => {
    setBodyLines(bodyLines.filter((_, i) => i !== index));
  };

  const handleBodyLineChange = (index: number, value: string) => {
    const newBodyLines = [...bodyLines];
    newBodyLines[index] = value;
    setBodyLines(newBodyLines);
  };

  const handlePrintCustomLabel = () => {
    const currentLabelConfig = DYMO_LABEL_SIZES.find(s => s.id === selectedLabelSizeId);
    let printClassName: string;

    if (selectedLabelSizeId === "custom") {
      // For truly custom dimensions, we might not have a pre-defined CSS class.
      // We can construct a generic one or advise the user that print may need browser scaling.
      // For this implementation, we'll use a base class and rely on the inline styles for preview.
      // The print accuracy for truly custom sizes without matching CSS would depend on browser scaling.
      // Or, we can try to create a dynamic class name, but it requires CSS injection or many pre-defined classes.
      // As a simplification, if "custom" is chosen, the specific print class might be less effective.
      // We can pass the base and let the @page CSS for the PrintModal try to handle it generally.
      // Let's build a class name like label-print-custom-WxH
      // printClassName = `label-print-base label-print-custom-${String(labelWidthInches).replace('.', '_')}x${String(labelHeightInches).replace('.', '_')}in`;
      // For simplicity, we will use the selected one if available, otherwise a default.
      // For truly dynamic print, JavaScript would need to inject a @page rule with the custom size.
      // This is complex, so we'll stick to using the pre-defined classes. If 'custom' is chosen,
      // it will use the *dimensions* for preview, but print might use the *closest known CSS class* or default.
      // For this iteration, if 'custom' is selected, it will use the dimensions for preview, and
      // we'll pass a generic or the default print class.
      printClassName = `label-print-base ${defaultSize.printClassName}`; // Fallback for custom if no exact match
       // A better approach for custom might be to not pass a specific dimension class and let user scale in print dialog
       // Or, find the closest predefined label.
       // For now, let's use the default for custom prints for simplicity of CSS.
    } else {
      printClassName = `label-print-base ${currentLabelConfig?.printClassName || defaultSize.printClassName}`;
    }
    

    onOpenPrintModal(
      'Custom Designed Label',
      <PrintableCustomLabel
        widthInches={labelWidthInches}
        heightInches={labelHeightInches}
        title={title}
        bodyLines={bodyLines}
        footer={footer}
        barcodeData={barcodeData} 
        showBarcode={showBarcode}   
        fontSizePt={fontSizePt}
      />,
      printClassName
    );
  };
  
  const inputClass = "w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm";
  const labelClass = "block text-sm font-medium text-slate-700 mb-1";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Controls Column */}
      <div className="md:col-span-1 space-y-4">
        <div>
          <label htmlFor="labelPreset" className={labelClass}>Label Size Preset:</label>
          <select 
            id="labelPreset" 
            value={selectedLabelSizeId} 
            onChange={(e) => handleLabelSizeChange(e.target.value)} 
            className={inputClass}
          >
            {DYMO_LABEL_SIZES.map(size => (
              <option key={size.id} value={size.id}>{size.name}</option>
            ))}
            <option value="custom">Custom Dimensions Below</option>
          </select>
        </div>

        {selectedLabelSizeId === "custom" && (
            <>
                <div>
                <label htmlFor="labelWidth" className={labelClass}>Custom Width (inches):</label>
                <input type="number" id="labelWidth" value={labelWidthInches} onChange={(e) => setLabelWidthInches(parseFloat(e.target.value) || 0)} step="0.01" min="0.5" className={inputClass} />
                </div>
                <div>
                <label htmlFor="labelHeight" className={labelClass}>Custom Height (inches):</label>
                <input type="number" id="labelHeight" value={labelHeightInches} onChange={(e) => setLabelHeightInches(parseFloat(e.target.value) || 0)} step="0.01" min="0.5" className={inputClass} />
                </div>
            </>
        )}
        
        <div>
          <label htmlFor="fontSize" className={labelClass}>Font Size (pt):</label>
          <input type="number" id="fontSize" value={fontSizePt} onChange={(e) => setFontSizePt(parseInt(e.target.value) || 8)} step="1" min="6" max="24" className={inputClass} />
        </div>
        <div>
          <label htmlFor="title" className={labelClass}>Title:</label>
          <input type="text" id="title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
        </div>
        
        <div className="space-y-2">
          <label className={labelClass}>Body Lines:</label>
          {bodyLines.map((line, index) => (
            <div key={index} className="flex items-center gap-2">
              <input type="text" value={line} onChange={(e) => handleBodyLineChange(index, e.target.value)} className={`${inputClass} flex-grow`} />
              <button type="button" onClick={() => handleRemoveBodyLine(index)} aria-label="Remove line" className="p-1">
                <XCircleIconMini />
              </button>
            </div>
          ))}
          <Button type="button" variant="ghost" onClick={handleAddBodyLine} className="text-sm flex items-center !text-emerald-600 hover:!text-emerald-700 hover:!bg-emerald-50">
            <PlusCircleIconMini /> Add Body Line
          </Button>
        </div>

        <div>
          <label htmlFor="footer" className={labelClass}>Footer:</label>
          <input type="text" id="footer" value={footer} onChange={(e) => setFooter(e.target.value)} className={inputClass} />
        </div>
        
        <div className="flex items-center gap-2 mt-2">
            <input type="checkbox" id="showBarcode" checked={showBarcode} onChange={(e) => setShowBarcode(e.target.checked)} className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]" />
            <label htmlFor="showBarcode" className="text-sm text-slate-700">Show Barcode</label>
        </div>
        {showBarcode && (
            <div>
                <label htmlFor="barcodeData" className={labelClass}>Barcode Data:</label>
                <textarea id="barcodeData" value={barcodeData} onChange={(e) => setBarcodeData(e.target.value)} rows={2} className={inputClass} placeholder="Enter data for barcode (e.g. Ticket ID, SKU)" />
            </div>
        )}

        <Button onClick={handlePrintCustomLabel} variant="primary" className="w-full mt-4">
          Generate & Print Custom Label
        </Button>
      </div>

      {/* Preview Column */}
      <div className="md:col-span-2 bg-slate-100 p-4 rounded-lg shadow-inner flex flex-col items-center justify-center min-h-[300px]">
        <h3 className="text-lg font-semibold text-slate-700 mb-3 self-start">Live Preview</h3>
        <div className="transform scale-100"> 
           <PrintableCustomLabel
            widthInches={labelWidthInches}
            heightInches={labelHeightInches}
            title={title}
            bodyLines={bodyLines}
            footer={footer}
            barcodeData={barcodeData} 
            showBarcode={showBarcode}   
            fontSizePt={fontSizePt}
          />
        </div>
        <p className="text-xs text-slate-500 mt-3 text-center">
          Note: Preview is an approximation. Actual print may vary. 
          Dimensions: {labelWidthInches}" x {labelHeightInches}".
        </p>
      </div>
    </div>
  );
};
